/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationCard Component
 * ----------------
 * Displays an individual application or application package in a
 * beautifully styled card layout. Shows logo, name, status, and
 * description, with smooth hover transitions and color themes.
 *
 * The card supports multiple interactive actions — such as:
 *  • Open  → View full application details
 *  • Edit  → Modify app information
 *  • Credentials  → Generate OAuth2 credentials
 *  • Delete  → Remove the application
 *  • Install  → Trigger installation setup
 *
 * Props:
 * - app: Object containing app data (title, description, logo, state, etc.)
 * - onOpen, onEdit, onDelete, onInstall, onGenerateCredentials: Action handlers
 * - showEdit/showDelete/showOpen/showInstall/showGenerateCredentials: Control visibility of buttons
 *
 * Theming:
 * - Uses `ThemeContext` for consistent colors, spacing, and radius across the UI.
 */

import React, { useEffect } from 'react';
import { useTheme } from '../theme/ThemeContext';
import '../styles/ApplicationCard.css'

const stateColors = {
    published: 'green',
    draft: 'orange'
};

export default function ApplicationCard({ app, onOpen, onEdit, onGenerateCredentials, onDelete, onInstall, showEdit = true, showDelete = true, showOpen = true, showInstall = true, showGenerateCredentials = true, installed = false, showPublish = false, published = false, onPublish = null, customAction = null, extraActions = [] }) {
    const { theme } = useTheme();
    const [localPublished, setLocalPublished] = React.useState(!!published);
    useEffect(() => setLocalPublished(!!published), [published]);

    // Handle card click (not on icons)
    const handleCardClick = (e) => {
        if (e.target.closest('.app-card-actions')) return;
        if (onOpen) onOpen(app);
    };

    return (
        <div
            className="application-card"
            onClick={handleCardClick}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.08)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.03)';
            }}
            style={{
                background: '#fff',
                position: 'relative',
                cursor: 'pointer',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '16px',
                transition: 'all 0.25s ease',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                padding: '24px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '280px',
                // aspectRatio: '3/2', // Optional: enforced aspect ratio if desired, but might conflict with flex grid height. 
                // Using minHeight is safer for "visual consistency" without breaking grid alignment.
            }}
        >
            {/* Header: Logo & Title */}
            <div className="d-flex align-items-center mb-3">
                {app.logoUrl ? (
                    <img
                        src={app.logoUrl}
                        alt={app.title}
                        style={{
                            height: 48,
                            width: 48,
                            objectFit: 'contain',
                            borderRadius: '12px',
                            marginRight: 16,
                            background: '#f8f9fa',
                            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.03)'
                        }}
                    />
                ) : (
                    <div style={{
                        height: 48,
                        width: 48,
                        borderRadius: '12px',
                        marginRight: 16,
                        background: theme.primary + '15',
                        color: theme.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px'
                    }}>
                        <i className="bi bi-grid-fill"></i>
                    </div>
                )}

                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="fw-bold fs-5 text-truncate" style={{ color: '#2c3e50', marginBottom: '2px' }}>
                        {app.title}
                    </div>
                    {/* Status Badge */}
                    <span
                        style={{
                            fontSize: '11px',
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            background: app.state === 'published' ? '#dcfce7' : '#fff7ed',
                            color: app.state === 'published' ? '#166534' : '#c2410c',
                            display: 'inline-block',
                            marginRight: '8px'
                        }}
                    >
                        {app.state}
                    </span>
                    {app.version && (
                        <span
                            style={{
                                fontSize: '11px',
                                padding: '4px 10px',
                                borderRadius: '20px',
                                fontWeight: 600,
                                background: '#f1f5f9',
                                color: '#475569',
                                display: 'inline-block'
                            }}
                        >
                            v{app.version}
                        </span>
                    )}
                </div>
            </div>

            {/* Description */}
            <div
                className="mb-4 text-muted"
                title={app.description}
                style={{
                    fontSize: '14px',
                    lineHeight: '1.6',
                    flex: '1 1 auto',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                }}
            >
                {app.description || "No description provided."}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#f1f5f9', marginBottom: '16px' }}></div>

            {/* Action Icons (bottom) */}
            <div className="d-flex justify-content-between align-items-center app-card-actions flex-wrap gap-2">
                <div className="d-flex gap-2">
                    {showInstall && !installed && onInstall && (
                        <IconAction
                            icon="bi-cloud-arrow-down"
                            label="Install"
                            color={theme.primary}
                            onClick={e => { e.stopPropagation(); onInstall(app); }}
                        />
                    )}
                    {installed && (
                        <IconAction
                            icon="bi-check-circle-fill"
                            label="Installed"
                            color="#166534"
                            onClick={null}
                            style={{ cursor: 'default', background: '#dcfce7' }}
                        />
                    )}
                    {customAction && (
                        <IconAction
                            icon={customAction.icon}
                            label={customAction.label}
                            color={customAction.color || theme.primary}
                            onClick={e => { e.stopPropagation(); customAction.onClick(); }}
                        />
                    )}
                    {extraActions.map((action, idx) => (
                        <IconAction
                            key={idx}
                            icon={action.icon}
                            label={action.label}
                            color={action.color || theme.primary}
                            onClick={e => { e.stopPropagation(); action.onClick(); }}
                        />
                    ))}
                    {showOpen && (
                        <IconAction
                            icon="bi-box-arrow-up-right"
                            label="Open"
                            color={theme.primary}
                            onClick={e => { e.stopPropagation(); onOpen(app); }}
                        />)
                    }
                    {showGenerateCredentials && (
                        <IconAction
                            icon="bi bi-key"
                            label="Credentials"
                            color={theme.primary}
                            onClick={e => { e.stopPropagation(); onGenerateCredentials(app); }}
                        />)
                    }
                </div>

                <div className="d-flex gap-2">
                    {showPublish && (
                        <IconAction
                            icon={localPublished ? "bi-check-circle-fill" : "bi-cloud-arrow-up"}
                            label={localPublished ? "Published" : "Publish"}
                            color={localPublished ? "#166534" : theme.primary}
                            onClick={localPublished ? null : e => { e.stopPropagation(); if (typeof onPublish === 'function') onPublish(app); }}
                            style={{ cursor: localPublished ? "default" : "pointer", opacity: localPublished ? 1 : 1, background: localPublished ? '#dcfce7' : '#f3f6fb' }}
                        />
                    )}
                    {showEdit && (
                        <IconAction
                            icon="bi-pencil"
                            label="Edit"
                            color={theme.primary}
                            onClick={e => { e.stopPropagation(); onEdit(app); }}
                        />)}
                    {showDelete && (
                        <IconAction
                            icon="bi-trash"
                            label="Delete"
                            color="#dc3545"
                            onClick={e => { e.stopPropagation(); onDelete(app); }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

// Reusable IconAction component with tooltip
function IconAction({ icon, label, color, onClick, style }) {
    return (
        <span
            className="app-action-icon"
            style={{
                color,
                background: "#f3f6fb",
                padding: 7,
                borderRadius: "50%",
                cursor: "pointer",
                fontSize: 20,
                position: "relative",
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
                transition: "background .16s, color .14s"
            }}
            tabIndex={0}
            onClick={onClick}
            onMouseEnter={e => e.currentTarget.style.background = '#e9ecef'}
            onMouseLeave={e => e.currentTarget.style.background = style?.background || '#f3f6fb'}
        >
            <i className={`bi ${icon}`}></i>
            <span className="action-tooltip">{label}</span>
        </span>
    );
}